/**
 * Chats API — target chats for the mailing engine.
 *
 * Reads/writes the user's chat list. The "import" endpoint resolves Telegram
 * references via the user's connected account, so the picker has to handle
 * 412 (no account) and 429 (FloodWait) gracefully.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api, ApiError } from "./client";

export type PeerType = "channel" | "chat" | "user";

export interface Chat {
  id: number;
  title: string;
  chat_ref: string;
  peer_type: PeerType;
  peer_id: number;
  topic_message_id: number | null;
  guarantor: string | null;
  guarantor_auto_detected: boolean;
  custom_template_id: number | null;
  custom_schedule_enabled: boolean;
  custom_posts_per_day: number;
  custom_interval_minutes: number;
  is_enabled: boolean;
  sort_order: number;
  consecutive_failures: number;
  paused_until: string | null;
  last_posted_at: string | null;
  created_at: string;
}

export interface ResolvePreviewItem {
  original: string;
  ok: boolean;
  title: string | null;
  chat_ref: string | null;
  peer_type: PeerType | null;
  topic_id: number | null;
  needs_join: boolean;
  error: string | null;
}

export interface ImportResultItem {
  original: string;
  ok: boolean;
  chat: Chat | null;
  error: string | null;
  needs_join: boolean;
  guarantor: string | null;
}

export interface ImportResponse {
  added: number;
  skipped_existing: number;
  failed: number;
  items: ImportResultItem[];
}

export interface ChatShareCreateResponse {
  token: string;
  url: string;
  chat_count: number;
  expires_at: string | null;
}

export interface ChatSharePreviewItem {
  title: string;
  chat_ref: string;
  peer_type: PeerType;
  topic_message_id: number | null;
}

export interface ChatSharePreviewResponse {
  token: string;
  owner_name: string | null;
  chat_count: number;
  expires_at: string | null;
  items: ChatSharePreviewItem[];
}

export interface DialogImportItem {
  peer_id: number;
  peer_type: "channel" | "chat";
  title: string;
  chat_ref: string;
}

export interface ChatPatch {
  title?: string;
  guarantor?: string | null;
  custom_template_id?: number | null;
  custom_schedule_enabled?: boolean;
  custom_posts_per_day?: number;
  custom_interval_minutes?: number;
  is_enabled?: boolean;
}

export type BulkActionType =
  | "enable"
  | "disable"
  | "delete"
  | "assign_template"
  | "clear_template";

export interface BulkActionBody {
  chat_ids: number[];
  action: BulkActionType;
  template_id?: number | null;
}

// ============================================================
// Queries
// ============================================================

export function useChats() {
  return useQuery<Chat[], ApiError>({
    queryKey: ["chats"],
    queryFn: () => api.get<Chat[]>("/chats"),
    staleTime: 30_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useResolvePreview() {
  return useMutation<
    { items: ResolvePreviewItem[] },
    ApiError,
    { refs: string[]; account_id?: number }
  >({
    mutationFn: (body) => api.post("/chats/resolve", body),
  });
}

export function useImportChats() {
  const qc = useQueryClient();
  return useMutation<
    ImportResponse,
    ApiError,
    { refs: string[]; account_id?: number; detect_guarantor?: boolean }
  >({
    mutationFn: (body) => api.post("/chats/import", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useCreateChatShare() {
  return useMutation<ChatShareCreateResponse, ApiError, { chat_ids?: number[] | null }>({
    mutationFn: (body) => api.post("/chats/share", body),
  });
}

export function useChatSharePreview(token: string | null) {
  return useQuery<ChatSharePreviewResponse, ApiError>({
    queryKey: ["chats", "share", token],
    queryFn: () => api.get<ChatSharePreviewResponse>(`/chats/share/${token}`),
    enabled: Boolean(token),
    staleTime: 30_000,
    retry: false,
  });
}

export function useImportChatShare() {
  const qc = useQueryClient();
  return useMutation<ImportResponse, ApiError, { token: string; account_id: number }>({
    mutationFn: ({ token, account_id }) => api.post(`/chats/share/${token}/import`, { account_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chats"] }),
  });
}

export function useImportAccountDialogs() {
  const qc = useQueryClient();
  return useMutation<ImportResponse, ApiError, { items: DialogImportItem[] }>({
    mutationFn: (body) => api.post("/chats/import-dialogs", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useUpdateChat(chatId: number) {
  const qc = useQueryClient();
  return useMutation<Chat, ApiError, ChatPatch>({
    mutationFn: (body) => api.patch(`/chats/${chatId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (chatId) => api.delete(`/chats/${chatId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useBulkAction() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, BulkActionBody>({
    mutationFn: (body) => api.post("/chats/bulk", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}

export function useReorderChats() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { chat_ids: number[] }>({
    mutationFn: (body) => api.post("/chats/reorder", body),
    onMutate: async (vars) => {
      // Optimistic reorder
      await qc.cancelQueries({ queryKey: ["chats"] });
      const prev = qc.getQueryData<Chat[]>(["chats"]);
      if (prev) {
        const byId = new Map(prev.map((c) => [c.id, c]));
        const next = vars.chat_ids
          .map((id, idx) => {
            const c = byId.get(id);
            return c ? { ...c, sort_order: idx } : null;
          })
          .filter((c): c is Chat => !!c);
        qc.setQueryData(["chats"], next);
      }
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      const c = ctx as { prev?: Chat[] } | undefined;
      if (c?.prev) qc.setQueryData(["chats"], c.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });
}
